import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../features/auth/context';
import { useToast } from '../hooks/useToast';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authApi } from '../features/auth/api';
import Table from '../components/ui/table';
import type { Column } from '../components/ui/table';
import Button from '../components/ui/button';
import Drawer from '../components/Drawer';
import { JourneyType } from '../types/journey';
import JourneyDisplay from '../components/JourneyDisplay';
import JourneyRouteMap, { RouteStop } from '../features/maps/JourneyRouteMap';
import { fetchJourneyDetail } from '../features/journeys/api';
import ConfirmationDialog from '../components/ConfirmationDialog';
import { summarizeTransportModes } from '../shared/transport';
import PageContainer from '../components/PageContainer';
import MiniButton from '../components/MiniButton';

interface ComparedJourneyRow {
  id: string;
  title: string;
  duration_days: number;
  time_spent_traveling: number;
  time_spent_in_places: number;
  number_of_stops: number;
  transportation_cost: number;
  transportation_emissions: number;
  comments?: string;
  created_at: string;
  journey_data: any;
  input_parameters: any;
}

const PAGE_SIZE = 20;

type SortKey = keyof ComparedJourneyRow;

type SortState = { key: SortKey; dir: 'asc' | 'desc' };

const JourneyComparisonPage: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // ?open=<journeyId>: deep-link that auto-opens that journey's editor drawer.
  // Used by the My Trips Journey detail page's "Edit route" button.
  const openId = searchParams.get('open');

  const [rows, setRows] = useState<ComparedJourneyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortState>({ key: 'created_at', dir: 'desc' }); // Default: newest first
  const [selected, setSelected] = useState<ComparedJourneyRow | null>(null);
  const [selectedJourneyDetail, setSelectedJourneyDetail] = useState<JourneyType | null>(null);
  const [editing, setEditing] = useState<ComparedJourneyRow | null>(null);
  const [editForm, setEditForm] = useState({ title: '', comments: '' });
  const [deleting, setDeleting] = useState<ComparedJourneyRow | null>(null);
  const [drawerConfirmClose, setDrawerConfirmClose] = useState(false);
  const [editState, setEditState] = useState<{ mode: 'view' | 'edit'; awaitingApproval: boolean }>({ mode: 'view', awaitingApproval: false });
  const [discardFn, setDiscardFn] = useState<(() => void) | null>(null);

  /**
   * Memoized callback for registering the discard function.
   * CRITICAL: Must be memoized to prevent infinite loop!
   * Without useCallback, this creates a new function reference on every render,
   * which triggers the useEffect in EditableJourney, causing re-render loops.
   */
  const handleRegisterDiscard = React.useCallback((fn: () => void) => {
    setDiscardFn(() => fn);
  }, []);

  /**
   * Memoized callback for edit state changes.
   * Must be memoized to prevent unnecessary re-renders.
   */
  const handleEditStateChange = React.useCallback((s: { mode: 'view' | 'edit'; awaitingApproval: boolean }) => {
    setEditState(s);
  }, []);

  /**
   * Ordered stops of the open journey reduced to the minimal shape the map
   * needs. lat/lng arrive as strings (Prisma Decimal serialized) so we coerce
   * here and drop any stop missing coordinates.
   */
  const routeStops = useMemo<RouteStop[]>(() => {
    const stops = selectedJourneyDetail?.stops ?? [];
    return [...stops]
      .sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0))
      .map((s: any) => ({
        id: s.id,
        lat: parseFloat(s.latitude),
        lng: parseFloat(s.longitude),
        label: s.city || s.location || '',
      }))
      .filter((s) => isFinite(s.lat) && isFinite(s.lng));
  }, [selectedJourneyDetail]);

  const handleSaveJourney = useCallback(async (journey: JourneyType) => {
    // Save journey to user's collection (existing functionality)
    try {
      showToast({
        title: 'Success',
        message: 'Journey saved to your collection',
        type: 'success'
      });
    } catch (error) {
      showToast({
        title: 'Error',
        message: 'Failed to save journey',
        type: 'error'
      });
    }
  }, [showToast]);

  // Removed handleSaveChanges - EditableJourney now handles review submission internally

  const handleRowClick = async (row: ComparedJourneyRow) => {
    setSelected(row);
    // Fetch detailed journey data for editing
    try {
      const journeyDetail = await fetchJourneyDetail(row.id);
      setSelectedJourneyDetail(journeyDetail);
    } catch (error) {
      console.error('Failed to fetch journey detail:', error);
      showToast({
        title: 'Error',
        message: 'Failed to load journey details',
        type: 'error'
      });
    }
  };

  useEffect(() => {
    if (!authLoading && !user) {
      showToast({ title: 'Authentication Required', message: 'Only logged in users can access Journey Comparison. Please login.', type: 'info' });
      navigate('/login');
    }
  }, [authLoading, user, navigate, showToast]);

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      const res = await authApi.get('/journeys');
      // Expect array of journeys from backend
      const data = Array.isArray(res.data) ? res.data : res.data.journeys;
      const normalized: ComparedJourneyRow[] = (data || []).map((j: any) => {
        // Use backend-provided time totals (no frontend recomputation)
        const backendTravelHours = Number(j?.timeSpentTravelingHours);
        const backendPlacesHours = Number(j?.timeSpentInPlacesHours);

        const timeSpentTravelingHours = Number.isFinite(backendTravelHours) ? backendTravelHours : 0;
        const timeSpentInPlacesHours = Number.isFinite(backendPlacesHours) ? backendPlacesHours : 0;

        return {
          id: j.id,
          title: j.title || 'Untitled',
          duration_days: j.durationDays ?? 0,
          time_spent_traveling: timeSpentTravelingHours,
          time_spent_in_places: timeSpentInPlacesHours,
          number_of_stops: j.stops?.length ?? 0,
          transportation_cost: j.totalTransportCostEur ?? 0,
          transportation_emissions: j.totalTransportCo2eKg ?? 0,
          // Keep user comments if present in the API response
          comments: j.comments ?? '',
          created_at: j.createdAt || j.created_at || new Date().toISOString(),
          journey_data: j, // Now the full journey object in v2 format
          input_parameters: j.inputParameters ?? j.input_parameters ?? null,
        };
      });
      setRows(normalized);
    } catch (e) {
      showToast({ title: 'Error', message: 'Failed to load comparison list.', type: 'error' });
      } finally {
      setLoading(false);
    }
  }, [showToast]);

  /**
   * Memoized callback for when journey is saved.
   * Refreshes the selected journey detail and the table rows.
   * Defined after fetchRows to avoid "used before declaration" error.
   */
  const handleJourneySaved = useCallback(async (saved: any) => {
    try {
      // Keep drawer open; refresh the selected detail and the table list
      const detail = await fetchJourneyDetail(saved.id);
      setSelectedJourneyDetail(detail);
      // If this was a create, update the selected row id/title for the drawer header
      setSelected((prev) => prev ? { 
        ...prev, 
        id: detail.id, 
        title: detail.title || prev.title,
        journey_data: detail 
      } : prev);
      await fetchRows();
    } catch (_) {
      // Best-effort; ignore errors here
    }
  }, [fetchRows]);

  /**
   * Memoized callback for saving journey to collection.
   */
  const handleSaveJourneyToCollection = useCallback(() => {
    if (selectedJourneyDetail) {
      handleSaveJourney(selectedJourneyDetail);
    }
  }, [selectedJourneyDetail, handleSaveJourney]);

  useEffect(() => {
    if (user && !authLoading) fetchRows();
  }, [user, authLoading, fetchRows]);

  const sortedRows = useMemo(() => {
    const sorted = [...rows].sort((a, b) => {
      const aVal = (a as any)[sort.key];
      const bVal = (b as any)[sort.key];
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return sort.dir === 'asc' ? -1 : 1;
      if (bVal == null) return sort.dir === 'asc' ? 1 : -1;
      if (typeof aVal === 'number' && typeof bVal === 'number') return sort.dir === 'asc' ? aVal - bVal : bVal - aVal;
      return sort.dir === 'asc' ? String(aVal).localeCompare(String(bVal)) : String(bVal).localeCompare(String(aVal));
    });
    return sorted;
  }, [rows, sort]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return sortedRows.slice(start, start + PAGE_SIZE);
  }, [sortedRows, page]);

  // ?open=<journeyId>: open that journey's drawer once rows are loaded, then
  // drop the param so closing the drawer sticks and it doesn't reopen.
  useEffect(() => {
    if (!openId || rows.length === 0) return;
    const row = rows.find((r) => r.id === openId);
    if (row) {
      setSelected(row);
      fetchJourneyDetail(row.id)
        .then((d) => setSelectedJourneyDetail(d))
        .catch(() => showToast({ title: 'Error', message: 'Failed to load journey details', type: 'error' }));
    }
    const next = new URLSearchParams(searchParams);
    next.delete('open');
    setSearchParams(next, { replace: true });
  }, [openId, rows, searchParams, setSearchParams, showToast]);

  const pageCount = Math.max(1, Math.ceil(sortedRows.length / PAGE_SIZE));

  const onSort = (key: SortKey) => {
    setSort((prev) => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' });
  };

  // Two-line sortable header renderer with inline sort icon
  const renderHeader = (line1: string, line2: string, key: SortKey) => {
    const hasSecond = line2 && line2.trim() !== '' && line2 !== '\u00A0';
    const SortedIcon = () => (
      <>
        <span className="text-slate-400 group-hover:text-emerald-500 text-xs">↕</span>
        {sort.key === key && (
          <span className="text-emerald-600 font-bold">{sort.dir === 'asc' ? '↑' : '↓'}</span>
        )}
      </>
    );

    return (
      <button
        className="text-left hover:text-emerald-700 transition-colors cursor-pointer font-medium group leading-tight"
        onClick={() => onSort(key)}
        title="Click to sort"
      >
        {hasSecond ? (
          <>
            <span className="block">{line1}</span>
            <span className="inline-flex items-center gap-1">
              <span>{line2}</span>
              <SortedIcon />
            </span>
          </>
        ) : (
          <>
            <span className="inline-flex items-center gap-1">
              <span>{line1}</span>
              <SortedIcon />
            </span>
            <span className="block">&nbsp;</span>
          </>
        )}
      </button>
    );
  };

  const narrowColClass = "max-w-[15ch] whitespace-normal break-words";
  const titleColClass = "max-w-[20ch] sm:max-w-[40ch] sticky left-0 bg-[var(--surface)] whitespace-normal break-words";
  const commentsColClass = "w-40";

  // Format created at date for display
  const formatCreatedAt = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    // Reset time to midnight for comparison
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const yesterdayOnly = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());
    
    if (dateOnly.getTime() === todayOnly.getTime()) {
      return 'Today';
    } else if (dateOnly.getTime() === yesterdayOnly.getTime()) {
      return 'Yesterday';
    } else {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                     'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${months[date.getMonth()]} ${date.getDate()}`;
    }
  };

  const handleEdit = (journey: ComparedJourneyRow) => {
    setEditing(journey);
    setEditForm({ title: journey.title, comments: journey.comments || '' });
  };

  const handleDelete = (journey: ComparedJourneyRow) => {
    setDeleting(journey);
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    
    try {
      await authApi.delete(`/journeys/${deleting.id}`);
      showToast({ title: 'Success', message: 'Journey deleted successfully!', type: 'success' });
      setDeleting(null);
      
      // Close the drawer if the deleted journey was the one being displayed
      if (selected?.id === deleting.id) {
        setSelected(null);
        setSelectedJourneyDetail(null);
      }
      
      fetchRows(); // Refresh the list
    } catch (error: any) {
      const msg = error?.response?.data?.error?.message || error.message || 'Failed to delete journey';
      showToast({ title: 'Error', message: msg, type: 'error' });
    }
  };

  const cancelDelete = () => {
    setDeleting(null);
  };

  const handleSaveEdit = async () => {
    if (!editing) return;
    
    if (!editForm.title.trim()) {
      showToast({ title: 'Validation Error', message: 'Title is required', type: 'error' });
      return;
    }
    
    try {
      await authApi.put(`/journeys/${editing.id}`, editForm);
      showToast({ title: 'Success', message: 'Journey updated successfully!', type: 'success' });
      setEditing(null);
      setEditForm({ title: '', comments: '' });
      fetchRows(); // Refresh the list
    } catch (error: any) {
      const msg = error?.response?.data?.error?.message || error.message || 'Failed to update journey';
      showToast({ title: 'Error', message: msg, type: 'error' });
    }
  };

  const handleCancelEdit = () => {
    setEditing(null);
    setEditForm({ title: '', comments: '' });
  };

  const columns: Column<ComparedJourneyRow>[] = [
    {
      key: 'title',
      header: renderHeader('Title', '\u00A0', 'title'),
      className: titleColClass,
      render: (r: ComparedJourneyRow) => {
        const modes = summarizeTransportModes(r.journey_data);
        const friendsUsed = Boolean(r.input_parameters?.includeFriends);
        return (
          <div className="text-left whitespace-normal break-words">
            <div className="mb-1">
              <button
                onClick={() => handleRowClick(r)}
                className="text-emerald-700 font-medium hover:text-emerald-800 cursor-pointer bg-transparent border-none p-0 text-left"
                title="Click to open journey"
              >
                {r.title}
              </button>
            </div>
            <div className="mb-1 flex gap-1">
              <MiniButton
                label="Open"
                onClick={() => handleRowClick(r)}
                hoverFillColor="#dcfce7"
                hoverOutlineColor="#bbf7d0"
                hoverTextColor="#166534"
              />
              <MiniButton
                label="Delete"
                onClick={() => handleDelete(r)}
              />
            </div>
            <span className="inline-flex items-center gap-1 align-middle">
              {modes.map(({ emoji, label }) => (
                <span key={label} title={label}>{emoji}</span>
              ))}
              {friendsUsed && <span title="Friends & Family">🫂</span>}
            </span>
          </div>
        );
      }
    },
    {
      key: 'duration_days',
      header: renderHeader('Duration', '(days)', 'duration_days'),
      className: narrowColClass,
    },
    {
      key: 'time_spent_traveling',
      header: renderHeader('Travel', 'hours', 'time_spent_traveling'),
      className: narrowColClass,
    },
    {
      key: 'time_spent_in_places',
      header: renderHeader('Stationary', 'hours', 'time_spent_in_places'),
      className: narrowColClass,
    },
    {
      key: 'number_of_stops',
      header: renderHeader('Places', 'visited', 'number_of_stops'),
      className: narrowColClass,
    },
    {
      key: 'transportation_cost',
      header: renderHeader('Transport', 'Cost', 'transportation_cost'),
      className: narrowColClass,
      render: (r: ComparedJourneyRow) => `${r.transportation_cost} EUR`,
    },
    {
      key: 'transportation_emissions',
      header: renderHeader('Transport', 'emissions', 'transportation_emissions'),
      className: narrowColClass,
      render: (r: ComparedJourneyRow) => `${r.transportation_emissions} kgCO₂e`,
    },
    {
      key: 'created_at',
      header: renderHeader('Created', 'At', 'created_at'),
      className: narrowColClass,
      render: (r: ComparedJourneyRow) => formatCreatedAt(r.created_at)
    },
    {
      key: 'comments',
      header: (
        <button 
          className="flex items-start gap-1 hover:text-emerald-700 transition-colors cursor-pointer font-medium group leading-tight"
          onClick={() => onSort('comments')}
          title="Click to sort"
        >
          <span className="block">Comments</span>
          <span className="text-slate-400 group-hover:text-emerald-500 text-xs">↕</span>
          {sort.key === 'comments' && (
            <span className="text-emerald-600 font-bold">
              {sort.dir === 'asc' ? '↑' : '↓'}
            </span>
          )}
        </button>
      ),
      className: commentsColClass,
      render: (r: ComparedJourneyRow) => (
        <div
          className="max-h-20 overflow-y-auto text-sm leading-5 break-words"
          title={r.comments || 'No comments'}
          style={{ lineHeight: '1.25rem' }}
        >
          {r.comments || 'No comments'}
        </div>
      )
    },
  ];

  if (authLoading || !user) return null;

  return (
    <PageContainer maxWidth="wide">
      <div className="flex items-center justify-between mb-4">
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>Journey Comparison</h1>
      </div>

      {loading ? (
        <div className="text-slate-600">Loading...</div>
      ) : (
        <>
          <div className="table-compact"><Table columns={columns} data={pagedRows} emptyMessage="No journeys compared yet." /></div>
          
          {/* Edit Form Modal */}
          {editing && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[2001]" onClick={handleCancelEdit}>
              <div className="bg-white rounded-lg p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                <h3 className="text-lg font-semibold mb-4">Edit Journey</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
                    <input
                      type="text"
                      value={editForm.title}
                      onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      onKeyDown={(e) => e.key === 'Escape' && handleCancelEdit()}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Comments</label>
                    <textarea
                      value={editForm.comments}
                      onChange={(e) => setEditForm(prev => ({ ...prev, comments: e.target.value }))}
                      rows={3}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      onKeyDown={(e) => e.key === 'Escape' && handleCancelEdit()}
                    />
                  </div>
                </div>
                <div className="flex gap-2 mt-6">
                  <Button variant="primary" onClick={handleSaveEdit}>
                    Save Changes
                  </Button>
                  <Button variant="secondary" onClick={handleCancelEdit}>
                    Cancel
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => handleDelete(editing)}
                    className="delete-soft"
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          )}
          
          {/* Delete Confirmation Dialog */}
          {deleting && (
            <ConfirmationDialog
              title="Delete Journey"
              message={`Are you sure you want to delete "${deleting.title}"? This action cannot be undone.`}
              onConfirm={confirmDelete}
              onCancel={cancelDelete}
              confirmText="Delete"
              cancelText="Cancel"
              confirmButtonStyle="danger"
            />
          )}
        </>
      )}

      <div className="flex items-center justify-between mt-4">
        <div className="text-sm text-slate-600">Page {page} of {pageCount}</div>
        <div className="flex gap-2">
          <Button variant="secondary" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</Button>
          <Button variant="secondary" disabled={page >= pageCount} onClick={() => setPage((p) => p + 1)}>Next</Button>
        </div>
      </div>

      <Drawer
        open={!!selected}
        onClose={() => {
          if (editState.mode === 'edit' || editState.awaitingApproval) {
            setDrawerConfirmClose(true);
          } else {
            setSelected(null);
            setSelectedJourneyDetail(null);
          }
        }}
        title={selected?.title || ''}
        width="50vw"
        headerActions={selected ? (
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => handleEdit(selected)}>Settings</Button>
          </div>
        ) : null}
      >
        <div className="p-4 space-y-4">
          {/* Map pinned below the drawer header while the journey details
              scroll beneath it. The Drawer body is the scroll container; the
              opaque background keeps scrolled content from showing through.
              z-30 keeps the map above the journey timeline's stop badges and
              edit controls (z-10/z-20) as they scroll past — dialogs live in a
              separate page-level stacking context (z 3000) and stay on top. */}
          {selected && selectedJourneyDetail && routeStops.length > 0 && (
            <div className="sticky top-0 z-30 pb-3 bg-white">
              <JourneyRouteMap stops={routeStops} />
            </div>
          )}
          {selected && selectedJourneyDetail && (
            <JourneyDisplay
              journey={selectedJourneyDetail}
              onSaveJourney={handleSaveJourneyToCollection}
              onJourneySaved={handleJourneySaved}
              onEditStateChange={handleEditStateChange}
              onRegisterDiscard={handleRegisterDiscard}
            />
          )}
          {selected && !selectedJourneyDetail && (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                <p className="text-gray-600">Loading journey details...</p>
              </div>
            </div>
          )}
        </div>
        
        {/* Add action buttons at the bottom, similar to Route Generator */}
        {selected && (
          <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
            <Button variant="secondary" onClick={() => {
              if (editState.mode === 'edit' || editState.awaitingApproval) {
                setDrawerConfirmClose(true);
              } else {
                setSelected(null);
                setSelectedJourneyDetail(null);
              }
            }}>
              Close
            </Button>
          </div>
        )}
      </Drawer>

      {/* Close Drawer Confirmation (when editing/approval) */}
      {drawerConfirmClose && (
        <ConfirmationDialog
          title="Discard changes and close?"
          message="You have unsaved changes. Do you want to discard them and close the drawer?"
          onConfirm={() => {
            try {
              discardFn && discardFn();
            } finally {
              setDrawerConfirmClose(false);
              setSelected(null);
              setSelectedJourneyDetail(null);
            }
          }}
          onCancel={() => setDrawerConfirmClose(false)}
          confirmText="Discard and Close"
          cancelText="Stay"
          confirmButtonStyle="danger"
        />
      )}
    </PageContainer>
  );
};

export default JourneyComparisonPage;
